import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Tabs, Descriptions, Tag, Space, Button } from 'antd';
import { DollarOutlined, CalendarOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { useToast } from '../../UI/ToastProvider';
import CustomerScheduleViewer from '../../PaymentSchedule/CustomerScheduleViewer';

function BookingShow() {
    const { id } = useParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [paying, setPaying] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const toast = useToast();

    const load = async () => {
        try {
            const res = await api.getBooking(id);
            setBooking(res.data);
        } catch (e) {
            setError('Failed to load booking');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const payNow = async () => {
        try {
            setPaying(true);
            await api.payBooking(id);
            await load();
            toast.show('Payment successful', { type: 'success' });
        } catch (e) {
            setError('Payment failed');
            toast.show('Payment failed', { type: 'error' });
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    if (!booking) {
        return <div className="max-w-4xl mx-auto p-6">{error || 'Not found'}</div>;
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'green';
            case 'pending': return 'orange';
            case 'cancelled': return 'red';
            case 'delivered': return 'blue';
            default: return 'default';
        }
    };

    const tabItems = [
        {
            key: 'details',
            label: 'Booking Details',
            children: (
                <div className="space-y-6">
                    <Card>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-xl font-semibold">{booking.vendor?.business_name}</h2>
                                <p className="text-gray-600">Booking #{booking.booking_number}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-orange-600">₹{booking.total_amount?.toLocaleString()}</div>
                                <Tag color={getStatusColor(booking.status)} className="mt-2">
                                    {booking.status?.toUpperCase()}
                                </Tag>
                            </div>
                        </div>

                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Event Date" icon={<CalendarOutlined />}>
                                {booking.event_date}
                            </Descriptions.Item>
                            <Descriptions.Item label="Event Time">
                                {booking.event_time}
                            </Descriptions.Item>
                            <Descriptions.Item label="Guest Count">
                                {booking.guest_count}
                            </Descriptions.Item>
                            <Descriptions.Item label="Event Type">
                                {booking.event_type || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Venue" span={2}>
                                {booking.venue || booking.delivery_address || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Special Instructions" span={2}>
                                {booking.special_instructions || 'None'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card title="Contact Information">
                        <Descriptions column={2}>
                            <Descriptions.Item label="Customer Name" icon={<UserOutlined />}>
                                {booking.customer_name || booking.user?.name}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone" icon={<PhoneOutlined />}>
                                {booking.customer_phone || booking.user?.phone || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email" span={2}>
                                {booking.customer_email || booking.user?.email}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </div>
            ),
        },
        {
            key: 'payments',
            label: 'Payment Schedule',
            children: (
                <CustomerScheduleViewer
                    bookingId={booking.id}
                    onPaymentSuccess={() => {
                        toast.show('Payment processed successfully', { type: 'success' });
                        load();
                    }}
                />
            ),
        },
    ];

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">Booking Details</h1>
            
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />
        </div>
    );
}

export default BookingShow;
